package org.matthewo.schema.games

import org.matthewo.api.MatchOutcome
import org.matthewo.api.MetricsDecodeAPI
import org.matthewo.math.Gaussian
import org.matthewo.math.minus
import org.matthewo.math.plus
import org.matthewo.math.scaledSigmoid
import org.matthewo.schema.BonusRPProvider
import org.matthewo.schema.RankingDetails
import org.matthewo.schema.RankingMetrics
import org.matthewo.schema.StandardMetrics
import org.matthewo.schema.games.DecodeScoreDetails.Classification
import org.matthewo.symbolprocessor.GameSchema
import org.matthewo.symbolprocessor.Metric
import org.matthewo.symbolprocessor.Period

@GameSchema(
    year = 2025,
    title = "Decode",
    allianceSize = 2,
    customDatabaseScore = true,
    hasRankingDetails = true,
    scoreDetailsClass = "DecodeScoreDetails",
)
interface MetricsDecode<T : Number> : StandardMetrics<T>, RankingMetrics<T>, BonusRPProvider {
    override val tbp1 get() = npTotal


    @Metric(Period.ALL, "Foul")
    override val penalty: T


    @Metric(Period.AUTO, "Classified", pointValue = CLASSIFIED.toDouble())
    val apClassified: T

    @Metric(Period.AUTO, "Overflow", pointValue = OVERFLOW.toDouble())
    val apOverflow: T

    @Metric(Period.AUTO, "Pattern", pointValue = PATTERN.toDouble())
    val apPattern: T

    @Metric(Period.AUTO, "Leave", pointValue = LEAVE.toDouble())
    val apLeave: T


    @Metric(Period.TELEOP, "Classified", pointValue = CLASSIFIED.toDouble())
    val tpClassified: T

    @Metric(Period.TELEOP, "Overflow", pointValue = OVERFLOW.toDouble())
    val tpOverflow: T

    @Metric(Period.TELEOP, "Depot", pointValue = DEPOT.toDouble())
    val tpDepot: T


    @Metric(Period.ENDGAME, "Pattern", pointValue = PATTERN.toDouble())
    val epPattern: T

    @Metric(Period.ENDGAME, "Base")
    val epBase: T


    @Metric(Period.ALL, "Bonus RP")
    val rpBonus: T

    @Metric(Period.ALL, "Movement RP")
    val rpMovement: T

    @Metric(Period.ALL, "Goal RP")
    val rpGoal: T

    @Metric(Period.ALL, "Pattern RP")
    val rpPattern: T

    override val bonusRP: List<Boolean>
        get() = [rpMovement, rpGoal, rpPattern].map { it.toDouble() >= 100 }


    companion object {
        const val CLASSIFIED = 3
        const val OVERFLOW = 1
        const val PATTERN = 2
        const val DEPOT = 1
        const val LEAVE = 3
        const val BASE_FULL = 10
        const val BASE_PARTIAL = 5
        const val BASE_BONUS = 10
    }
}

fun <T : Number> MutableMetricsDecode<T>.computeTotals() {
    rpBonus = rpMovement + rpGoal + rpPattern
    auto = apClassified + apOverflow + apPattern + apLeave
    teleop = tpClassified + tpOverflow + tpDepot
    endgame = epPattern + epBase
    npTotal = auto + teleop + endgame
    total = npTotal - penalty
}

interface DecodeScoreDetails {
    val randomization: Int
    val asPattern: List<Classification>
    val esPattern: List<Classification>
    val esRobot1: BaseLocation
    val esRobot2: BaseLocation

    enum class Classification(val code: String) {
        NONE("N"),
        PURPLE("P"),
        GREEN("G");

        companion object {
            fun fromCode(code: String) = entries.firstOrNull { it.code == code }
                ?: error("Invalid code: $code")
        }
    }

    enum class BaseLocation {
        NONE,
        PARTIAL,
        FULL,
    }
}

fun pattern(randomization: Int) = (1..3).flatMap { motif(randomization) }

fun motif(randomization: Int) = when (randomization) {
    0 -> [Classification.NONE, Classification.NONE, Classification.NONE]
    -1, 1 -> [Classification.GREEN, Classification.PURPLE, Classification.PURPLE]
    2 -> [Classification.PURPLE, Classification.GREEN, Classification.PURPLE]
    3 -> [Classification.PURPLE, Classification.PURPLE, Classification.GREEN]
    else -> error("$randomization is not a valid motif randomization")
}

object DecodeRankingDetails : RankingDetails<MetricsDecode<*>> {
    override fun combine(team1: MetricsDecode<*>, team2: MetricsDecode<*>) = MetricsDecodeAPI<Double>(team1, team2)

    override fun winProbability(alliance: MetricsDecode<*>?, opponent: MetricsDecode<*>?, playedFactor: Double): Double {
        if (null == alliance || null == opponent) return 0.5
        val a = alliance.npTotal.toDouble()
        val b = opponent.npTotal.toDouble()
        val diff = (a - b) / (a + b)
        if (!diff.isFinite()) return 0.5
        val predictor = when {
            playedFactor >= 4 -> scaledSigmoid(6.31709, 0.95)
            playedFactor >= 3 -> scaledSigmoid(6.21134, 0.9)
            playedFactor >= 2 -> scaledSigmoid(5.01799, 0.85)
            playedFactor >= 1 -> scaledSigmoid(4.75774, 0.8)
            else -> scaledSigmoid(5.73183, 0.7)
        }
        return predictor(diff)
    }

    override fun rp(outcome: MatchOutcome, score: MetricsDecode<*>?, project: Boolean): Number {
        val match = Gaussian.next(0.5, if (outcome == MatchOutcome.WIN) 0.25 else -0.25) + 1
        val bonus =
            if (project) score?.run {
                val rpMovement = Gaussian.next(20.0, rpMovement.toDouble() * match) > 100.0
                val rpPattern = Gaussian.next(60.0, rpPattern.toDouble() * match) > 100.0
                val rpGoal = Gaussian.next(50.0, rpGoal.toDouble() * match) > 100.0
                [rpMovement, rpGoal, rpPattern].sumOf { if (it) 1 else 0 }
            } ?: 0
            else score?.bonusRP?.sumOf { if (it) 1 else 0 } ?: 0
        val wlt = when (outcome) {
            MatchOutcome.WIN -> 3
            MatchOutcome.TIE -> 1
            MatchOutcome.LOSS -> 0
        }
        return bonus + wlt
    }

    override fun tbp1(score: MetricsDecode<*>) = score.npTotal
}
